import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, AlertTriangle, CheckCircle, DollarSign, TrendingUp, TrendingDown, FileText, Loader2, RefreshCw } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

function fmt(v) { return (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function YearEndClosingPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [year, setYear] = useState((new Date().getFullYear() - 1).toString());
  const [confirmText, setConfirmText] = useState('');

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/accounting/year-end/preview?fiscal_year=${year}`);
      setData(res.data);
    } catch { toast.error('Failed to load year-end preview'); }
    setLoading(false);
  };

  useEffect(() => { fetchPreview(); }, [year]);

  const executeClosing = async () => {
    if (confirmText !== `CLOSE ${year}`) {
      toast.error(`Type "CLOSE ${year}" to confirm`);
      return;
    }
    setClosing(true);
    try {
      await api.post('/accounting/year-end/close', { fiscal_year: year });
      toast.success(`Fiscal year ${year} has been closed`);
      setConfirmText('');
      fetchPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close fiscal year');
    }
    setClosing(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="year-end-closing-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg shadow-rose-200/50">
              <Lock size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Year-End Closing</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Close fiscal year and transfer net income to retained earnings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={year} onChange={e => setYear(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="fiscal-year-selector">
              {[...Array(6)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
            </select>
            <Button variant="outline" size="sm" onClick={fetchPreview}><RefreshCw size={14} /></Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-4 border-error border-t-transparent rounded-full animate-spin" /></div>
        ) : data && (
          <>
            {/* Status Banner */}
            {data.already_closed ? (
              <Card className="border-success/30 bg-success/10">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                    <CheckCircle size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="font-bold text-success" data-testid="closed-status">Fiscal Year {year} is Closed</p>
                    <p className="text-sm text-success">Closed on {data.closed_at?.slice(0, 10)}. Closing journal entry has been posted.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-warning/30 bg-warning/10">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-warning" />
                  </div>
                  <div>
                    <p className="font-bold text-warning" data-testid="open-status">Fiscal Year {year} is Open</p>
                    <p className="text-sm text-warning">Review the summary below and close when ready.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Financial Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-success/30">
                <CardContent className="p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-success/20 mx-auto mb-2 flex items-center justify-center"><TrendingUp size={22} className="text-success" /></div>
                  <p className="text-xs text-muted-foreground uppercase">Total Revenue</p>
                  <p className="text-2xl font-bold text-success" data-testid="total-revenue">{fmt(data.total_revenue)}</p>
                </CardContent>
              </Card>
              <Card className="border-error/30">
                <CardContent className="p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-error/20 mx-auto mb-2 flex items-center justify-center"><TrendingDown size={22} className="text-error" /></div>
                  <p className="text-xs text-muted-foreground uppercase">Total Expenses</p>
                  <p className="text-2xl font-bold text-error" data-testid="total-expenses">{fmt(data.total_expenses)}</p>
                </CardContent>
              </Card>
              <Card className={`border-2 ${data.net_income >= 0 ? 'border-success/30 bg-success/10' : 'border-error/30 bg-error/10'}`}>
                <CardContent className="p-5 text-center">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${data.net_income >= 0 ? 'bg-success/20' : 'bg-error/20'}`}>
                    <DollarSign size={22} className={data.net_income >= 0 ? 'text-success' : 'text-error'} />
                  </div>
                  <p className="text-xs text-muted-foreground uppercase">Net Income</p>
                  <p className={`text-2xl font-bold ${data.net_income >= 0 ? 'text-success' : 'text-error'}`} data-testid="net-income">{fmt(data.net_income)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Transfers to Retained Earnings</p>
                </CardContent>
              </Card>
            </div>

            {/* Expense Breakdown */}
            {data.expense_breakdown?.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Expense Breakdown</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm" data-testid="expense-breakdown-table">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">% of Total</th>
                    </tr></thead>
                    <tbody>
                      {data.expense_breakdown.map((e, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-4 py-3 text-foreground">{e.category}</td>
                          <td className="px-4 py-3 text-right font-medium text-error">{fmt(e.amount)}</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">{data.total_expenses > 0 ? ((e.amount / data.total_expenses) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Closing Entry Preview */}
            {data.closing_entry_preview && !data.already_closed && (
              <Card className="border-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText size={14} /> Closing Journal Entry Preview</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Account</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Debit</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Credit</th>
                    </tr></thead>
                    <tbody>
                      {data.closing_entry_preview.lines.map((l, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-4 py-3 text-foreground">{l.account} - {l.account_name}</td>
                          <td className="px-4 py-3 text-right font-medium text-success">{l.debit > 0 ? fmt(l.debit) : '-'}</td>
                          <td className="px-4 py-3 text-right font-medium text-error">{l.credit > 0 ? fmt(l.credit) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Close Action */}
            {!data.already_closed && (
              <Card className="border-error/30 bg-error/10">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle size={20} className="text-error mt-0.5 shrink-0" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-bold text-error mb-1">Warning: This action is irreversible</p>
                      <p>Closing the fiscal year will generate a closing journal entry that transfers all revenue and expense account balances to Retained Earnings. This cannot be undone.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      placeholder={`Type "CLOSE ${year}" to confirm`}
                      value={confirmText}
                      onChange={e => setConfirmText(e.target.value)}
                      className="flex-1 border border-border rounded-lg px-3 py-2 text-sm"
                      data-testid="confirm-input"
                    />
                    <Button
                      variant="destructive"
                      onClick={executeClosing}
                      disabled={closing || confirmText !== `CLOSE ${year}`}
                      data-testid="close-year-btn"
                    >
                      {closing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Lock size={14} className="mr-1" />}
                      Close Fiscal Year {year}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
